/**
 * A created-per-day trend, charted (L1) — shared by the incident and alert
 * sections, which publish the same `DefenderTrendPoint[]` shape and carry the
 * same honesty obligations.
 *
 * **Why a zero here is real, and a gap is not a zero.** Unlike a sampled probe
 * trend, this read is a complete enumeration of the window
 * (`nova.defender.models.DefenderTrendPoint`), so a day inside the returned
 * range with nothing in it is a *measured* zero and is plotted as one — that is
 * genuinely good news and must not be hidden. What is absent is days Graph never
 * returned: those fall outside the range and simply make the line shorter, which
 * is why nothing is padded at the ends.
 *
 * A day missing from the *middle* of the range should be impossible, so it is
 * treated as the anomaly it would be: the row is emitted with a `null` count, and
 * Recharts (`connectNulls` off by default) leaves the line broken there. A
 * straight interpolation across it would invent volume; a zero would invent
 * quiet. §5.
 *
 * **`truncated` makes the whole line a floor** (§8), so the caption says so and
 * the tooltip reads "at least" rather than a bare count.
 */

import { Chart } from "@norican/dataviz";
import { EmptyState } from "@norican/react";
import type { ReactNode } from "react";

import { ChartCard } from "@/components/board";
import type { DefenderTrendPoint } from "@/lib/api";

import { formatCount, formatDay, tooltipValue, TRUNCATED_NOTE } from "./status";

/** One plotted day. `count: null` is a day the backend did not report at all. */
interface TrendRow {
  day: string;
  label: string;
  count: number | null;
}

export interface TrendPanelProps {
  /** Card heading. One series, so no legend — this names it (§6). */
  title: string;
  /** The payload's trend, oldest first. */
  points: readonly DefenderTrendPoint[];
  /** What one unit is: "incidents", "alerts". */
  noun: string;
  /** Chart accessible name — required on every `Chart.Container` (§6). */
  ariaLabel: string;
  /** Board-unique table-twin id from `TWIN_IDS`. */
  twinId: string;
  /** `collected_at` of the panel this trend came from. */
  since?: string | null;
  /** Staleness threshold for the `Freshness` dot, in ms. */
  staleAfterMs?: number;
  /** True when the panel's paging stopped at NOVA's cap — counts are floors. */
  truncated: boolean;
  /** Extra caption text, e.g. the window this trend covers. */
  caption?: ReactNode;
  /** What to say when the trend is empty — never a flat line at zero. */
  emptyTitle: string;
  emptyDescription: string;
}

export function TrendPanel({
  title,
  points,
  noun,
  ariaLabel,
  twinId,
  since,
  staleAfterMs,
  truncated,
  caption,
  emptyTitle,
  emptyDescription,
}: TrendPanelProps) {
  const rows = buildTrendRows(points);
  const measured = rows.filter((row) => row.count != null);
  const total = measured.reduce((sum, row) => sum + (row.count ?? 0), 0);
  const peak = measured.reduce<number | null>(
    (best, row) => (best == null || (row.count ?? 0) > best ? (row.count ?? 0) : best),
    null,
  );

  return (
    <ChartCard<TrendRow>
      title={title}
      caption={
        rows.length === 0 ? (
          "Nothing was counted"
        ) : (
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span>
              {formatCount(total)} {noun}
              {truncated ? " or more" : ""} across {formatCount(measured.length)} days · busiest day{" "}
              {formatCount(peak)}
            </span>
            {caption && <span>· {caption}</span>}
          </span>
        )
      }
      since={since}
      staleAfterMs={staleAfterMs}
      tableTwin={{
        id: twinId,
        ariaLabel: `${title} — every day and its count`,
        data: rows,
        getRowId: (row) => row.day,
        columns: [
          { id: "day", header: "Day (UTC)", accessor: (row) => row.label },
          {
            id: "count",
            header: truncated ? "Count (floor)" : "Count",
            align: "end",
            accessor: (row) => row.count,
            cell: (row) => <span className="tabular-nums">{formatCount(row.count)}</span>,
          },
          {
            id: "measured",
            header: "Measured",
            accessor: (row) => (row.count == null ? "not reported" : "yes"),
          },
        ],
      }}
    >
      {rows.length === 0 ? (
        <EmptyState size="sm" title={emptyTitle} description={emptyDescription} />
      ) : (
        <div className="flex flex-col gap-3">
          <Chart.Container
            config={{ count: { label: noun, color: "var(--norican-accent)" } }}
            className="h-64"
            aria-label={ariaLabel}
          >
            <Chart.LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
              <Chart.XAxis {...Chart.axisProps} dataKey="label" />
              <Chart.YAxis {...Chart.axisProps} width={44} allowDecimals={false} />
              <Chart.Tooltip
                content={
                  <Chart.TooltipContent
                    // `tooltipValue` first, never `Number(value)`: a day the
                    // backend did not report arrives as `undefined`, and
                    // `Number(null)` would render a fabricated 0.
                    formatter={(value) =>
                      `${truncated ? "at least " : ""}${formatCount(tooltipValue(value))} ${noun}`
                    }
                  />
                }
              />
              <Chart.Line
                {...Chart.seriesDefaults}
                dataKey="count"
                type="monotone"
                stroke="var(--color-count)"
                strokeWidth={2}
                dot={false}
              />
            </Chart.LineChart>
          </Chart.Container>

          {measured.length < rows.length && (
            <p className="text-fg-muted text-sm">
              {formatCount(rows.length - measured.length)} days inside the range were not reported
              at all, so the line breaks there. A day with nothing in it is reported as a measured
              zero, so a break means the read is short — not that the estate was quiet.
            </p>
          )}

          {truncated && <p className="text-fg-muted text-sm">{TRUNCATED_NOTE}</p>}
        </div>
      )}
    </ChartCard>
  );
}

/**
 * Project the payload's points onto a dense day axis.
 *
 * Only the days *between* the first and last reported day are filled in, and
 * only with `null`. Nothing is added beyond the ends: a short read is meant to
 * render as a shorter line, and padding it would fabricate a window Graph never
 * answered for.
 */
function buildTrendRows(points: readonly DefenderTrendPoint[]): TrendRow[] {
  if (points.length === 0) {
    return [];
  }
  const byDay = new Map(points.map((point) => [point.day, point.count]));
  const days = [...byDay.keys()].sort((a, b) => a.localeCompare(b));
  const first = Date.parse(`${days[0]}T00:00:00Z`);
  const last = Date.parse(`${days[days.length - 1]}T00:00:00Z`);

  // A day that will not parse cannot be placed on an axis; fall back to plotting
  // exactly what arrived rather than guessing where it belongs.
  if (Number.isNaN(first) || Number.isNaN(last) || last < first) {
    return days.map((day) => ({ day, label: formatDay(day), count: byDay.get(day) ?? null }));
  }

  const rows: TrendRow[] = [];
  const DAY_MS = 86_400_000;
  for (let instant = first; instant <= last; instant += DAY_MS) {
    const day = new Date(instant).toISOString().slice(0, 10);
    rows.push({ day, label: formatDay(day), count: byDay.get(day) ?? null });
  }
  return rows;
}
